import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ConsentFormHeader from 'components/molecules/consent-form/header';
import ConsentPatientInfo from 'components/molecules/consent-form/patient-info';
import ConsentSectionTitle from 'components/molecules/consent-form/section-title';
import ConsentFormFooter from 'components/molecules/consent-form/footer';
import ConsentSignatureBlock from 'components/molecules/consent-form/signature-block';
import ConsentFormCheckbox from 'components/molecules/consent-form/checkbox';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { isValidGuid } from 'infrastructure/functions/is-valid-guid';
import { showResult } from 'infrastructure/hooks/utils';
import { useTranslation } from 'react-i18next';
import BaseLoader from 'components/atoms/base-loader';
import { usePatientInsurance } from 'infrastructure/hooks/patient/use-patient-insurance';
import { DateFormatter, prepareDoctorName } from 'infrastructure/functions';
import { useConsentCcmForm } from 'infrastructure/hooks/patient/use-consent-ccm-form';
import { Routes } from 'router/routes';
import { CcmStatuses } from 'infrastructure/enums';
import { useDispatch, useSelector } from 'react-redux';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';

import s from './styles.module.scss';

import type { Patient } from 'infrastructure/classes/patient/patient';
import type { TPatientInsurances } from 'infrastructure/interfaces';
import type { RootState } from 'store';

interface IProps {
  isEnrolled: boolean;
  patientInfo?: Patient;
  insurances: TPatientInsurances;
  onConsent: (val: boolean) => void;
  consentInfo: string | null;
  patientConsentInfo: string | null;
}

const PcmTemplate: React.FC<IProps> = ({
  isEnrolled,
  patientInfo,
  insurances,
  onConsent,
  consentInfo,
  patientConsentInfo,
}) => {
  const { t } = useTranslation();

  const fields = useMemo(() => {
    const insuranceNumber =
      insurances.length > 0 ? insurances[0].number : t('labels.noInsurance');

    return [
      {
        name: t('labels.patientName'),
        value: `${patientInfo?.loginInfo?.firstName} ${patientInfo?.loginInfo?.lastName}`,
      },
      {
        name: t('labels.dob'),
        value: DateFormatter({
          date: patientInfo?.loginInfo?.birthDate,
        }),
      },
      {
        name: t('labels.primaryInsurance'),
        value: insuranceNumber,
      },
      {
        name: t('labels.memberId'),
        value: `${patientInfo?.guid}`,
      },
      {
        name: t('labels.phoneNumber'),
        value: `${patientInfo?.loginInfo?.phone}`,
      },
      {
        name: t('labels.preferredEmail'),
        value: `${patientInfo?.loginInfo?.email}`,
      },
    ];
  }, [t, patientInfo, insurances]);

  const benefitsList = t('pcmConsentForm.benefitsList', {
    returnObjects: true,
  });
  const acknowledgmentList = t('pcmConsentForm.acknowledgmentList', {
    returnObjects: true,
  });

  return (
    <div className={s.page}>
      <ConsentFormHeader
        title={t('pcmConsentForm.title')}
        subTitle={t('pcmConsentForm.subTitle')}
      />
      <ConsentPatientInfo fields={fields} />
      <ConsentSectionTitle>
        {t('pcmConsentForm.informedConsentTitle')}
      </ConsentSectionTitle>
      <p>{t('pcmConsentForm.informedConsent')}</p>
      <ConsentSectionTitle>
        {t('pcmConsentForm.benefitsTitle')}
      </ConsentSectionTitle>
      <ul>
        {benefitsList.map((benefit, i) => (
          <li key={i}>{benefit}</li>
        ))}
        {t('pcmConsentForm.benefitsListNote')}
      </ul>
      <ConsentSectionTitle>
        {t('pcmConsentForm.beforeSigningUpTitle')}
      </ConsentSectionTitle>
      <p>
        {t('pcmConsentForm.beforeSigningUpText')}
        <br />
        <br />
        {t('pcmConsentForm.beforeSigningUpText2')}
      </p>
      <ConsentSectionTitle>
        {t('pcmConsentForm.rightsTitle')}
      </ConsentSectionTitle>
      <p>
        {t('pcmConsentForm.rightsText')}
        <br />
        <br />
        {t('pcmConsentForm.rightsTextNote')}
      </p>
      <ConsentSectionTitle>
        {t('pcmConsentForm.acknowledgmentTitle')}
      </ConsentSectionTitle>
      <p>
        {t('pcmConsentForm.acknowledgmentText')}
        <ul>
          {acknowledgmentList.map((acknowledgment, i) => (
            <li key={i}>{acknowledgment}</li>
          ))}
        </ul>
      </p>
      <ConsentFormCheckbox
        value={patientInfo?.ccmConsentFormStatus}
        canPressNo={!isEnrolled}
        label={t('pcmConsentForm.checkboxLabel')}
        onCheck={onConsent}
      />
      <p>
        {consentInfo}
        <br />
        {patientConsentInfo}
      </p>
      <ConsentSignatureBlock
        name={t('pcmConsentForm.signaturePatient')}
        underline={false}
      />
      <ConsentSignatureBlock name={t('pcmConsentForm.signatureAgency')} />
      <ConsentFormFooter />
    </div>
  );
};

const CcmTemplate: React.FC<IProps> = ({
  isEnrolled,
  patientInfo,
  insurances,
  onConsent,
  consentInfo,
  patientConsentInfo,
}) => {
  const { t } = useTranslation();

  const fields = useMemo(() => {
    const insuranceNumber =
      insurances.length > 0 ? insurances[0].number : t('labels.noInsurance');

    return [
      {
        name: t('labels.patientName'),
        value: `${patientInfo?.loginInfo?.firstName} ${patientInfo?.loginInfo?.lastName}`,
      },
      {
        name: t('labels.dob'),
        value: DateFormatter({
          date: patientInfo?.loginInfo?.birthDate,
        }),
      },
      {
        name: t('labels.primaryInsurance'),
        value: insuranceNumber,
      },
      {
        name: t('labels.memberId'),
        value: `${patientInfo?.guid}`,
      },
      {
        name: t('labels.phoneNumber'),
        value: `${patientInfo?.loginInfo?.phone}`,
      },
      {
        name: t('labels.preferredEmail'),
        value: `${patientInfo?.loginInfo?.email}`,
      },
    ];
  }, [t, patientInfo, insurances]);

  const benefitsList = t('ccmConsentForm.benefitsList', {
    returnObjects: true,
  });
  const acknowledgmentList = t('ccmConsentForm.acknowledgmentList', {
    returnObjects: true,
  });

  return (
    <div className={s.page}>
      <ConsentFormHeader
        title={t('ccmConsentForm.title')}
        subTitle={t('ccmConsentForm.subTitle')}
      />
      <ConsentPatientInfo fields={fields} />
      <ConsentSectionTitle>
        {t('ccmConsentForm.informedConsentTitle')}
      </ConsentSectionTitle>
      <p>{t('ccmConsentForm.informedConsent')}</p>
      <ConsentSectionTitle>
        {t('ccmConsentForm.benefitsTitle')}
      </ConsentSectionTitle>
      <ul>
        {benefitsList.map((benefit, i) => (
          <li key={i}>{benefit}</li>
        ))}
        {t('ccmConsentForm.benefitsListNote')}
      </ul>
      <ConsentSectionTitle>
        {t('ccmConsentForm.beforeSigningUpTitle')}
      </ConsentSectionTitle>
      <p>
        {t('ccmConsentForm.beforeSigningUpText')}
        <br />
        <br />
        {t('ccmConsentForm.beforeSigningUpText2')}
      </p>
      <ConsentSectionTitle>
        {t('ccmConsentForm.rightsTitle')}
      </ConsentSectionTitle>
      <p>
        {t('ccmConsentForm.rightsText')}
        <br />
        <br />
        {t('ccmConsentForm.rightsTextNote')}
      </p>
      <ConsentSectionTitle>
        {t('ccmConsentForm.acknowledgmentTitle')}
      </ConsentSectionTitle>
      <p>
        {t('ccmConsentForm.acknowledgmentText')}
        <ul>
          {acknowledgmentList.map((acknowledgment, i) => (
            <li key={i}>{acknowledgment}</li>
          ))}
        </ul>
      </p>
      <ConsentFormCheckbox
        value={patientInfo?.ccmConsentFormStatus}
        canPressNo={!isEnrolled}
        label={t('ccmConsentForm.checkboxLabel')}
        onCheck={onConsent}
      />
      <p>
        {consentInfo}
        <br />
        {patientConsentInfo}
      </p>
      <ConsentSignatureBlock
        name={t('ccmConsentForm.signaturePatient')}
        underline={false}
      />
      <ConsentSignatureBlock
        name={t('ccmConsentForm.signatureAuthorized')}
        showReason
      />
      <ConsentSignatureBlock name={t('ccmConsentForm.signatureAgency')} />
      <ConsentFormFooter />
    </div>
  );
};

const ConsentFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { patientGuid } = useParams();

  useEffect(() => {
    if (!patientGuid || !isValidGuid(patientGuid)) {
      showResult(t('errors.invalidGuid'));
      navigate(Routes.notFound, { replace: true });
    }
  }, [patientGuid, navigate]);

  const [insurances, setInsurances] = useState<TPatientInsurances>([]);

  const { getPatientDetailsWithCcmData: ccmDataLoading } = useSelector(
    (state: RootState) => state.loading.effects.adminPanelPatients,
  );

  const {
    adminPanelPatients: { getPatientDetailsWithCcmData },
  } = useDispatch();

  const { loading: insuranceLoading, loadPatientInsurance } =
    usePatientInsurance({
      patientGuid: patientGuid!,
    });

  const {
    loading: patientInfoLoading,
    patientInfo,
    invalidateCache,
  } = usePatientInfo({
    patientGuid: patientGuid!,
  });

  const { loading: consentLoading, consentForm } = useConsentCcmForm();

  const { profile } = useSelector(
    (state: RootState) => state.adminPanelPatients.patient,
  );

  const loading = insuranceLoading || patientInfoLoading || ccmDataLoading;

  const isEnrolled = patientInfo?.ccmStatus === CcmStatuses.Enrolled;

  const showPcmTemplate = isEnrolled && patientInfo.pcmStatus;

  const consentInfo = profile?.consentFormData?.signedDate
    ? `${t('labels.consentObtainedBy')}: ${prepareDoctorName({
        title: profile?.consentFormData.signedBy?.title,
        firstName: profile?.consentFormData.signedBy?.firstName,
        lastName: profile?.consentFormData.signedBy?.lastName,
        professionType: profile?.consentFormData.signedBy?.professionType,
      })}, ${t('labels.on')}
  ${profile?.consentFormData?.signedDate}`
    : null;

  const patientConsentInfo = profile?.consentFormData?.signedDate
    ? `${t('labels.patientVerbalConsent')}: ${prepareDoctorName({
        title: profile?.title,
        firstName: profile?.firstName,
        lastName: profile?.lastName,
      })}
    , ${t('labels.on')}
    ${profile?.consentFormData?.signedDate}`
    : null;

  const handleConsent = useCallback(
    async (ccmConsentForm: boolean) => {
      await consentForm(patientGuid!, ccmConsentForm).then(() => {
        invalidateCache();
        navigate(Routes.adminPatient(patientGuid!), { replace: true });
      });
    },
    [patientGuid, consentForm],
  );

  useEffect(() => {
    if (patientGuid) {
      loadPatientInsurance().then((res) => {
        if (res) setInsurances(res);
      });
      getPatientDetailsWithCcmData(patientGuid);
    }
  }, [patientGuid]);

  return (
    <BaseLoader loading={loading}>
      <BaseSpinWrapper spinning={consentLoading}>
        {showPcmTemplate ? (
          <PcmTemplate
            isEnrolled={isEnrolled}
            patientInfo={patientInfo}
            insurances={insurances}
            onConsent={handleConsent}
            consentInfo={consentInfo}
            patientConsentInfo={patientConsentInfo}
          />
        ) : (
          <CcmTemplate
            isEnrolled={isEnrolled}
            patientInfo={patientInfo}
            insurances={insurances}
            onConsent={handleConsent}
            consentInfo={consentInfo}
            patientConsentInfo={patientConsentInfo}
          />
        )}
      </BaseSpinWrapper>
    </BaseLoader>
  );
};

export default ConsentFormPage;
